import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  colorTokens,
  getShadow,
  getSpacing,
  getTypography,
} from "../../../../../designTokens";
import type {
  GetOverviewExportStatusResponse,
  OverviewExportRecord,
} from "../../../../../Apis/overviewExportTypes";
import { formatExportExpiryDate } from "../../../../../utils/buildOverviewExportPayloads";
import { mergeHighlightedExportIntoHistory } from "../../../../../utils/mergeHighlightedExport";
import { ExportModalCloseButton } from "./ExportModalCloseButton";

const HISTORY_PAGE_SIZE = 20;

interface ExportHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchHistory: (params?: { limit?: number; offset?: number }) => Promise<{
    success: boolean;
    exports?: OverviewExportRecord[];
    count?: number;
  }>;
  /** GET …/export/:id — used when highlightExportId is not on the first page. */
  fetchExportById?: (
    exportId: string
  ) => Promise<GetOverviewExportStatusResponse>;
  entityLabel?: string;
  canDownloadInApp?: boolean;
  highlightExportId?: string;
  idPrefix?: string;
}

function formatHistoryHeading(entityLabel: string): string {
  return `${entityLabel} export history`
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatStatusLabel(status: string): string {
  switch (status) {
    case "QUEUED":
      return "Queued";
    case "PROCESSING":
      return "Processing";
    case "COMPLETED":
      return "Completed";
    case "FAILED":
      return "Failed";
    default:
      return status;
  }
}

export const ExportHistoryModal: React.FC<ExportHistoryModalProps> = ({
  isOpen,
  onClose,
  fetchHistory,
  fetchExportById,
  entityLabel = "export",
  canDownloadInApp = false,
  highlightExportId,
  idPrefix = "export-history-modal",
}) => {
  const [exports, setExports] = useState<OverviewExportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const highlightedRowRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHistory({ limit: HISTORY_PAGE_SIZE, offset: 0 });
      const history = res.exports ?? [];
      const focusedId = highlightExportId?.trim();
      if (
        !focusedId ||
        history.some((item) => item._id === focusedId) ||
        !fetchExportById
      ) {
        setExports(history);
        return;
      }

      try {
        const focusedRes = await fetchExportById(focusedId);
        setExports(
          mergeHighlightedExportIntoHistory(
            history,
            focusedRes.export,
            focusedId
          )
        );
      } catch {
        // GET /export/:id is owner-scoped (404 for others). Keep the page.
        setExports(history);
      }
    } catch {
      setError("Failed to load export history.");
      setExports([]);
    } finally {
      setLoading(false);
    }
  }, [fetchHistory, fetchExportById, highlightExportId]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!highlightExportId || loading) return;
    highlightedRowRef.current?.scrollIntoView?.({
      block: "nearest",
      behavior: "smooth",
    });
  }, [highlightExportId, loading, exports]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 10002,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: getSpacing("16px"),
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${idPrefix}-title`}
      id={`${idPrefix}-backdrop`}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "560px",
          maxHeight: "80vh",
          backgroundColor: colorTokens["Background/White"],
          borderRadius: getSpacing("16px"),
          boxShadow: getShadow("Shadow"),
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
        id={`${idPrefix}-container`}
      >
        <div
          style={{
            padding: getSpacing("24px"),
            borderBottom: `1px solid ${colorTokens["Borders/Light"]}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              ...getTypography("Headings/H3"),
              color: colorTokens["Text/Black"],
              margin: 0,
            }}
            id={`${idPrefix}-title`}
          >
            {formatHistoryHeading(entityLabel)}
          </div>
          <ExportModalCloseButton onClick={onClose} id={`${idPrefix}-close`} />
        </div>

        <div
          style={{ flex: 1, overflowY: "auto", padding: getSpacing("16px") }}
        >
          {loading && (
            <div
              style={{
                ...getTypography("Body/Medium"),
                color: colorTokens["Text/Grey"],
                textAlign: "center",
              }}
            >
              Loading...
            </div>
          )}
          {error && (
            <div
              style={{
                ...getTypography("Body/Medium"),
                color: colorTokens["Text/Red"],
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}
          {!loading && !error && exports.length === 0 && (
            <div
              style={{
                ...getTypography("Body/Medium"),
                color: colorTokens["Text/Grey"],
                textAlign: "center",
              }}
            >
              No exports yet.
            </div>
          )}
          {!loading &&
            exports.map((item) => {
              const isExpired =
                item.presignedUrlExpiresAt &&
                new Date(item.presignedUrlExpiresAt).getTime() < Date.now();
              const canDownload =
                canDownloadInApp &&
                item.status === "COMPLETED" &&
                Boolean(item.presignedUrl) &&
                item.downloadAvailable !== false &&
                !isExpired;
              const sentByEmail = item.deliveryMode === "EMAIL";
              const isHighlighted = highlightExportId === item._id;

              return (
                <div
                  key={item._id}
                  ref={isHighlighted ? highlightedRowRef : undefined}
                  id={`${idPrefix}-row-${item._id}`}
                  style={{
                    padding: getSpacing("12px"),
                    borderRadius: getSpacing("8px"),
                    border: isHighlighted
                      ? `2px solid ${colorTokens["Primary/Default"]}`
                      : `1px solid ${colorTokens["Borders/Light"]}`,
                    backgroundColor: isHighlighted
                      ? colorTokens["Status/Open/Bg"]
                      : "transparent",
                    display: "flex",
                    flexDirection: "column",
                    gap: getSpacing("4px"),
                    marginBottom: getSpacing("8px"),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        ...getTypography("Body/Medium"),
                        color: colorTokens["Text/Black"],
                      }}
                    >
                      {formatStatusLabel(item.status)}
                      {item.rowCount != null
                        ? ` · ${item.rowCount.toLocaleString()} rows`
                        : ""}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: getSpacing("12px"),
                      }}
                    >
                      {sentByEmail && item.status === "COMPLETED" ? (
                        <span
                          style={{
                            ...getTypography("Body/Small"),
                            color: colorTokens["Text/Grey"],
                          }}
                        >
                          Sent by email
                        </span>
                      ) : null}
                      {canDownload ? (
                        <a
                          href={item.presignedUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            ...getTypography("Links/Btn Text"),
                            color: colorTokens["Primary/Default"],
                          }}
                          id={`${idPrefix}-download-${item._id}`}
                        >
                          Download
                        </a>
                      ) : item.status === "COMPLETED" && isExpired ? (
                        <span
                          style={{
                            ...getTypography("Body/Small"),
                            color: colorTokens["Text/Light Grey"],
                          }}
                        >
                          Expired
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div
                    style={{
                      ...getTypography("Body/Small"),
                      color: colorTokens["Text/Light Grey"],
                    }}
                  >
                    Created: {formatExportExpiryDate(item.createdAt)}
                  </div>
                  {item.presignedUrlExpiresAt &&
                    item.status === "COMPLETED" &&
                    canDownloadInApp && (
                      <div
                        style={{
                          ...getTypography("Body/Small"),
                          color: colorTokens["Text/Light Grey"],
                        }}
                      >
                        Link expires:{" "}
                        {formatExportExpiryDate(item.presignedUrlExpiresAt)}
                      </div>
                    )}
                  {item.status === "FAILED" && item.error && (
                    <div
                      style={{
                        ...getTypography("Body/Small"),
                        color: colorTokens["Text/Red"],
                      }}
                    >
                      {item.error}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
